import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { buildSingleMoveChargesInvoice, legacySingleMoveStatement } from '@hopdrive/sdk-ar';
import DocumentDefinition from '@utils/pdfDocumentDefinition';
import pdfMake from 'pdfmake/build/pdfmake';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { getMoveInvoiceCharges } from '@features/moves/queries/getMoveInvoiceCharges.query.generated';

dayjs.extend(utc);

// importing fonts from the library breaks the build. This is a workaround to load the fonts from a CDN
const pdfMakeFonts = {
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

// No need for vfs_fonts import, just set the fonts
pdfMake.fonts = pdfMakeFonts;

export const createSingleMoveInvoice = async (invoice: any) => {
  try {
    // The totals, and the amount due, are sdk-ar's figures for the move's armoves.
    const amount = legacySingleMoveStatement(invoice.armoves);
    // Build the PDF
    const docDefinition = new DocumentDefinition(invoice, amount);
    const dd = docDefinition.create(invoice, amount);
    let name = 'invoice.pdf';
    try {
      if (invoice.armoves && invoice.armoves[0] && invoice.armoves[0].move && invoice.armoves[0].move.consumer_name) {
        name = `${invoice.armoves[0].move.id}_${invoice.armoves[0].move.consumer_name.replace(' ', '_')}_${dayjs.utc(dayjs(invoice.armoves[0].billable_datetime)).format(`MM-DD-YYYY`)}_invoice.pdf`;
      } else {
        name = `${invoice.armoves[0].move.id}_${dayjs.utc(dayjs(invoice.armoves[0].billable_datetime)).format(`MM-DD-YYYY`)}_invoice.pdf`;
      }
    } catch (err) {
      console.error('Could not parse invoice document name:', err);
    }
    pdfMake.createPdf(dd).download(name);
  } catch (err) {
    console.error('Failed to generate PDF of invoice:', err);
    throw new Error('Failed to generate PDF of invoice');
  }
};

/**
 * The single-move invoice PDF of a move billed through AR charges. sdk-ar's
 * buildSingleMoveChargesInvoice decides what prints, from the move's current
 * billing groups on an invoice the user can see; this only loads them and
 * renders the result.
 *
 * Resolves false, printing nothing, when there is nothing the user can print.
 * Rejects when the amount due cannot be read.
 */
export const createSingleMoveChargesInvoice = async (move: { id: number; consumer_name?: string | null }) => {
  const argroups = await getMoveInvoiceCharges({ moveId: move.id });
  const built = await buildSingleMoveChargesInvoice(argroups, move);
  if (!built) return false;

  const docDefinition = new DocumentDefinition(built.invoice, built.amount, undefined);
  // The layout is untyped JavaScript; its output is a pdfmake document.
  const dd = docDefinition.create(built.invoice, built.amount, undefined) as unknown as TDocumentDefinitions;
  pdfMake.createPdf(dd).download(built.fileName);
  return true;
};
